  import React, { useState, useCallback, useRef, useEffect } from "react";
  import { useLocation, useNavigate, useParams } from "react-router-dom";
  import { useSelector } from "react-redux";
  import _ from "lodash";
  import { Image } from "react-bootstrap";

  import InfiniteScroll from "react-infinite-scroller";
  import SwitchButton from "src/components/switchButton";
  import { ChatUserRow } from "./components/chatUserRow/index";
  import { ChatContainer } from "./components/chatContainer";
  import { EngageProvider, useEngageContext } from "./context/EngageContext";
  import { useChatUsersInfiniteQuery, useChatUserQuery } from "src/redux/actions/chatUsers";
  import { LiveViewSlider } from "./components/liveViewSlider";

  import cx from "classnames";
  import styles from "./styles.module.scss";
  import userRoleService from "src/utils/userRoles.service";

  function EngagePageContent() {
    const { selectedChatUser, setSelectedChatUser } = useEngageContext();

    const { id: pathId } = useParams<{ id?: string }>();

    const location = useLocation();
    const navigate = useNavigate();
      
    const query = new URLSearchParams(location.search);
    const [filter, setFilter] = useState(query.get("filter") || "all");
    const [global] = useState(query.get("global") === "true");
    const [globalState, setGlobalState] = useState(global);


    useEffect(() => {
      const desiredSearch = `?filter=${encodeURIComponent(filter)}&global=${String(globalState)}`;
      if (location.search !== desiredSearch) {
        navigate(`${location.pathname}${desiredSearch}`, { replace: true });
      }
    }, [filter, globalState, location.pathname, location.search, navigate]);

    const queryParams = { filter, global: globalState };
    const {
      data,
      isLoading,
      isFetching,
      fetchNextPage,
      hasNextPage,
      isFetchingNextPage,
    } = useChatUsersInfiniteQuery(queryParams);

    const chatUsers: Record<string, unknown>[] =
      (data?.pages?.flatMap((page) => (page as { records?: unknown[] }).records ?? []) ?? []) as Record<string, unknown>[];

      const firstPage = data?.pages?.[0] as { totalRecordsCount?: number; totalPages?: number; recordsPerPage?: number } | undefined;

      const totalRecords =
      firstPage?.totalRecordsCount ??
      (firstPage?.totalPages != null && firstPage?.recordsPerPage != null
        ? firstPage.totalPages * firstPage.recordsPerPage
        : chatUsers.length);

      const [showLiveViewSlider, setShowLiveViewSlider] = useState(false);

    const { data: pathChatUser, isSuccess: pathChatUserLoaded } = useChatUserQuery(pathId ?? null);

    React.useEffect(() => {
      if (pathId != null && pathId !== "" && pathChatUser != null && pathChatUserLoaded) {
        const idMatch =
          String(_.get(pathChatUser, "id")) === pathId || String(_.get(pathChatUser, "chatUserId")) === pathId;
        if (idMatch) {
          const currentId = selectedChatUser && (String(_.get(selectedChatUser, "id")) === pathId || String(_.get(selectedChatUser, "chatUserId")) === pathId);
          if (!currentId) {
            setSelectedChatUser(pathChatUser as Record<string, unknown>);
            return;
          }
        }
      }
      if (chatUsers.length === 0) return;
      if (pathId != null && pathId !== "") {
        const user = chatUsers.find(
          (chatUser) => String(_.get(chatUser, "id")) === pathId || String(_.get(chatUser, "chatUserId")) === pathId
        );
        if (user && selectedChatUser?.id !== user.id && _.get(user, "chatUserId") !== selectedChatUser?.id) {
          setSelectedChatUser(user);
          return;
        }
      }

      if (!selectedChatUser) {
        setSelectedChatUser(chatUsers[0]);
      }
    }, [chatUsers, pathId, pathChatUser, selectedChatUser, setSelectedChatUser]);

    const handleSelectChatUser = useCallback(
      (chatUser: Record<string, unknown>) => {
        setSelectedChatUser(chatUser);
        const id = chatUser?.id != null ? String(chatUser.id) : null;
        const search = `?filter=${encodeURIComponent(filter)}&global=${String(globalState)}`;
        if (id) {
          navigate(`/engage/${id}${search}`, { replace: true });
        }
      },
      [filter, globalState, navigate, setSelectedChatUser]
    );

    const loadMore = useCallback(() => {
      if (hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    const scrollParentRef = useRef<HTMLDivElement>(null);

    const FILTERS = [
      { id: "all", label: "My Recipients" },
      { id: "live", label: "Live Users" },
      { id: "unread", label: "Unread Chats" },
    ];

    const selectedFilterIndex = FILTERS.findIndex((f) => f.id === filter);

    const handleAllRecipientsChange = () => {
      const newGlobalState = !globalState;
      setGlobalState(newGlobalState);

      if (newGlobalState) {
        setFilter("all");
      }
    };

    return (
      <div className={cx(styles.engagePage)}>
        <div className={cx(styles.sidebar, {
          [styles.hideSidebar]: showLiveViewSlider
        })}>

          <div className={styles.sidebarHeader}>
            <div className={styles.title}>engage</div>
            <div className={styles.subTitle}>what was said and done</div>
          </div>

          <div className={styles.sidebarActions}>
            {(userRoleService.isAdmin() || userRoleService.canAccessGlobalRecipients()) && 
            (<div className={styles.actionItem}>
              <span className={styles.actionItemLabel}>Show All Recipients</span>
              <SwitchButton id="all-recipients" checked={globalState} handleCheck={handleAllRecipientsChange} />
            </div>)}
          </div>

          <div className={styles.sidebarFiltersContainer}>
            <div
              className={styles.sidebarFilters}
              style={{ ["--selected-index" as string]: Math.max(0, selectedFilterIndex) }}
            >
              <div className={styles.filterPill} aria-hidden />
              {FILTERS.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={cx(styles.filterItem, { [styles.selected]: filter === f.id })}
                  data-selected={filter === f.id}
                  onClick={() => setFilter(f.id)}
                >
                  {f.label}{filter === f.id ? ` ${totalRecords}` : ""}
                </button>
              ))}
            </div>



          </div>

          <div ref={scrollParentRef} className={styles.sidebarContent}>
            <InfiniteScroll
              pageStart={0}
              loadMore={loadMore}
              hasMore={(hasNextPage || isFetching) ?? false}
              useWindow={false}
              getScrollParent={() => scrollParentRef.current ?? undefined}
              loader={
                <div key="loader" className={styles.placeholderChatUserRowContainer}>
                  {(
                    _.times(3, (index) => <PlaceholderChatUserRow key={index} />)
                  )}
                </div>
              }
              >
              {
               _.size(chatUsers) > 0 && 
               selectedChatUser &&
               chatUsers.map((chatUser: Record<string, unknown>) => (
                <ChatUserRow
                  activeChatUser={selectedChatUser?.id as string | number | undefined}
                  key={String(chatUser.id)}
                  chatUser={chatUser}
                  name={String(chatUser?.firstName ?? "")}
                  icon={(chatUser?.contactDTO as { icon?: { thumbnail?: string } } | undefined)?.icon ?? null}
                  onSelect={handleSelectChatUser}
                  showLiveViewSlider={showLiveViewSlider}
                />
              ))}
              {!isFetching && !isLoading && !isFetchingNextPage && _.size(chatUsers) === 0 && (
                <div className={styles.emptyMessageContainer}>
                  <span className={styles.emptyMessageText}>No chats available</span>
                </div>
              )}
            </InfiniteScroll>
          </div>
        </div>

        <div className={styles.mainContent}>
          <ChatContainer 
          isLoading={isLoading}
          chatUser={selectedChatUser} 
          showLiveViewSlider={showLiveViewSlider}
          setShowLiveViewSlider={setShowLiveViewSlider} />
        </div>

        <div className={cx(styles.liveViewSlider, { [styles.showLiveViewSlider]: showLiveViewSlider })}>
            <LiveViewSlider show={showLiveViewSlider} onClose={() => setShowLiveViewSlider(false)} />
        </div>
      </div>
    );
  }

  const EngagePage = () => {
    return (
      <EngageProvider>
        <EngagePageContent />
      </EngageProvider>
    );
  };

  const PlaceholderChatUserRow = () => {
    return (
      <div className={cx(styles.placeholderChatUserRow, styles.placeholder)}>
      <div className={cx(styles.placeholderChatUserRowAvatar, styles.block)} />
      <div className={cx(styles.placeholderChatUserRowName, styles.block)} />
    </div>
    );
  };

export default EngagePage;